import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { VpIcon } from '@vtmn-play/react'
import WidgetErrorCard from 'components/common/cards/widget-error-card'
import WidgetLoading from 'components/common/cards/widget-loading'
import cloudNotAvailable from 'core/assets/images/icons/cloud_not_available.svg'
import { useApiGetWeeklyGmvProgress } from 'core/api/home/useApiGetWeeklyGmvProgress'
import { useFormatAmount } from 'core/hooks/useFormatAmount'
import { HomeContext } from 'pages/home/state/home-context'
import WeeklyGmvProgressCircle from './weekly-gmv-progress-circle'
import { isHomeApiEnabled } from 'core/api/home/home-api-utils'

const UnavailableAmount = ({ className }: { className?: string }) => (
  <span
    className={['mg-weekly-gmv-progress-value-unavailable', className].filter(Boolean).join(' ')}
    data-testid="weekly-gmv-progress-value-unavailable"
  >
    - -
  </span>
)

interface WeeklyGmvProgressCardProps {
  yearWeek: string
}

const WeeklyGmvProgressCard = ({ yearWeek }: WeeklyGmvProgressCardProps) => {
  const { t } = useTranslation()
  const formatAmount = useFormatAmount()
  const { channel, aggregation, storeId, teamId, store } = useContext(HomeContext)
  const currency = store?.currency ?? ''
  const { data, isLoading, isError } = useApiGetWeeklyGmvProgress(
    { channel, aggregation, storeId, teamId, week: yearWeek },
    isHomeApiEnabled(storeId, teamId, aggregation)
  )
  const { realizedInError = false, forecastInError = false, targetPercent = 0, realizedGmv, forecastGmv } = data ?? {}

  const isRealizedAndForecastUnavailable = realizedInError && forecastInError
  const showErrorCard = isError || data == null || isRealizedAndForecastUnavailable
  const showProgress = data != null && !realizedInError && !forecastInError

  if (isLoading) {
    return (
      <WidgetLoading
        className="mg-weekly-gmv-progress-card"
        dataTestId="weekly-gmv-progress-loading"
        skeletonCount={2}
      />
    )
  }

  if (showErrorCard) {
    return (
      <WidgetErrorCard
        titleKey="general.homepage.weekly-gmv-progress.title"
        messageKey={isRealizedAndForecastUnavailable ? 'general.homepage.weekly-gmv-progress.unavailable' : undefined}
        testId="weekly-gmv-progress-error"
      />
    )
  }

  return (
    <section className="mg-weekly-gmv-progress-card" data-testid="weekly-gmv-progress-card">
      <div
        className={`mg-weekly-gmv-progress-content${realizedInError || forecastInError ? ' mg-weekly-gmv-progress-content--with-error' : ''}`}
      >
        <div className="mg-weekly-gmv-progress-main">
          <WeeklyGmvProgressCircle targetPercent={targetPercent} unavailable={!showProgress} />

          <div className="mg-weekly-gmv-progress-values">
            <p className="mg-weekly-gmv-progress-amounts">
              <span className="vp-title-s" data-testid="weekly-gmv-progress-realized">
                {realizedInError ? <UnavailableAmount /> : formatAmount(realizedGmv, currency)}
              </span>
              <span className="mg-weekly-gmv-progress-forecast vp-body-m" data-testid="weekly-gmv-progress-forecast">
                {' / '}
                {forecastInError ? <UnavailableAmount /> : formatAmount(forecastGmv, currency)}
              </span>
            </p>
            <p className="mg-weekly-gmv-progress-label vp-caption">{t('general.homepage.weekly-gmv-progress.label')}</p>
          </div>
        </div>

        {realizedInError && (
          <p
            className="mg-weekly-gmv-progress-error-message vp-body-s"
            data-testid="weekly-gmv-progress-realized-error"
          >
            <img src={cloudNotAvailable} alt="" className="mg-weekly-gmv-progress-error-icon" aria-hidden />
            <span>{t('general.homepage.weekly-gmv-progress.unavailable-realized')}</span>
          </p>
        )}

        {forecastInError && (
          <p
            className="mg-weekly-gmv-progress-error-message vp-body-s"
            data-testid="weekly-gmv-progress-forecast-error"
          >
            <span className="mg-weekly-gmv-progress-error-icon-wrapper">
              <VpIcon {...({ name: 'warning-error', size: 16 } as any)} />
            </span>
            <span>{t('general.homepage.weekly-gmv-progress.unavailable-forecast')}</span>
          </p>
        )}
      </div>
    </section>
  )
}

export default WeeklyGmvProgressCard
